import React, { useState, useEffect, useCallback } from 'react';
import { Info, CheckCircle2, Lock } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore'; // Added Firestore imports
import { httpsCallable } from 'firebase/functions'; // Added Functions imports
import Navbar from './Navbar';
import TrustBar from './TrustBar';

// Import initialized Firebase services
import { db, functions } from '../firebase/config';

// Import Firebase helpers
import { signUp, signIn } from '../firebase/auth';
import { sendCustomPasswordResetEmail, sendCustomVerificationEmail } from '../firebase/authEmails';
import { createGiftCard } from '../firebase/giftcards';
import { uploadGiftCardImage } from '../firebase/storage';
import { formatPrice } from '../utils/formatPrice';
import {
  CHECKOUT_FEE,
  calculatePlatformFee,
  calculateSellerPayoutForTargetSellingPrice,
  calculateSellingPriceFromSellerPayout,
} from '../utils/pricing';

const PRIMARY_BUTTON_ENABLED =
  'bg-gradient-to-r from-orange-500 via-red-500 to-red-600 text-white shadow-[0_18px_40px_-22px_rgba(239,68,68,0.7)] hover:shadow-[0_22px_44px_-20px_rgba(239,68,68,0.75)]';
const PRIMARY_BUTTON_DISABLED = 'bg-gray-300 text-gray-500 cursor-not-allowed';
const PRIMARY_BUTTON_CLASS = `rounded-xl font-bold transition ${PRIMARY_BUTTON_ENABLED}`;
const WARM_CARD_CLASS = 'rounded-2xl border border-orange-100 bg-white shadow-[0_24px_70px_-45px_rgba(234,88,12,0.35)]';
const WARM_HIGHLIGHT_CLASS = 'rounded-2xl border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-rose-50';
const SELLER_TRUST_ITEMS = [
  '✓ Gratis je cadeaubon aanbieden',
  '🧾 Jij kiest je prijs',
  '🔍 Na onze check komt je cadeaubon live',
  '💸 Bon verkocht? Binnen 24 uur staat je geld op je rekening',
];



// ============================================================================
// STEP 1: Details
// ============================================================================
const SellStep1 = ({ initialData = {}, onNext }) => {
  const [brand, setBrand] = useState(initialData.brand || '');
  const [cardValue, setCardValue] = useState(initialData.cardValue || 100);
  const [selectedPreset, setSelectedPreset] = useState(initialData.selectedPreset || 'aanbevolen');
  const [customAmount, setCustomAmount] = useState(initialData.customAmount || '');
  const [useCustom, setUseCustom] = useState(initialData.useCustom || false);
  const [showSpecification, setShowSpecification] = useState(false);

  const brands = [
    'Albert Heijn', 'Bol.com', 'Coolblue', 'Douglas'
  ];
  const valueOptions = [20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95, 100, 125, 150];

  const getMinDiscount = (value) => {
    if (value < 40) return 2.00;
    return value * 0.05;
  };
  const getMaxSellingPrice = (value) => {
    const minDiscount = getMinDiscount(value);
    return value - minDiscount;
  };

  const maxSellingPrice = getMaxSellingPrice(cardValue);
  const maxSellerReceives = calculateSellerPayoutForTargetSellingPrice(maxSellingPrice);

  const calculatePreset = (targetPercentage) => {
    return maxSellerReceives * targetPercentage;
  };
  
  const presets = {
    snel: calculatePreset(0.85),
    aanbevolen: calculatePreset(0.92),
    max: maxSellerReceives
  };

  const getCurrentSellerPayout = () => {
    if (useCustom && customAmount) {
      const amount = parseFloat(customAmount);
      if (!isNaN(amount)) return amount;
    }
    return presets[selectedPreset];
  };

  const sellerPayout = getCurrentSellerPayout();
  const platformFee = calculatePlatformFee(sellerPayout);
  const buyerPrice = calculateSellingPriceFromSellerPayout(sellerPayout);
  const buyerTotal = buyerPrice + CHECKOUT_FEE;

  const isValidCustomAmount = () => {
    if (!customAmount) return true;
    const amount = parseFloat(customAmount);
    return amount >= 1 && amount <= maxSellerReceives;
  };

  const handleNext = () => {
    if (!brand || !isValidCustomAmount()) return;
    
    const stepData = {
      brand, cardValue, selectedPreset, customAmount, useCustom,
      sellerPayout, buyerPrice, maxSellerReceives
    };
    
    onNext(stepData);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-3">Verkoop je cadeaubon</h1>
        <p className="text-lg text-gray-600">Bepaal je prijs en ontvang je uitbetaling na verkoop.</p>
      </div>
      <div className="mb-8">
        <TrustBar items={SELLER_TRUST_ITEMS} />
      </div>
      <div className={`${WARM_CARD_CLASS} p-8`}>
        <div className="mb-6">
          <label className="block text-gray-900 font-semibold mb-2">Winkel</label>
          <select value={brand} onChange={(e) => setBrand(e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none text-lg">
            <option value="">Selecteer een winkel</option>
            {brands.map((b) => (<option key={b} value={b}>{b}</option>))}
          </select>
        </div>
        <div className="mb-8">
          <label className="block text-gray-900 font-semibold mb-2">Waarde</label>
          <select value={cardValue} onChange={(e) => setCardValue(parseFloat(e.target.value))} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none text-lg">
            {valueOptions.map((val) => (<option key={val} value={val}>{formatPrice(val)}</option>))}
          </select>
        </div>
        <div className="mb-8">
          <label className="block text-gray-900 font-semibold mb-4">Kies je uitbetaling</label>
          <div className="grid grid-cols-3 gap-2 mb-6 bg-gray-100 p-1 rounded-lg">
            {['snel', 'aanbevolen', 'max'].map(preset => (
              <button key={preset} onClick={() => { setSelectedPreset(preset); setUseCustom(false); }}
                className={`py-3 px-4 rounded-lg font-semibold transition ${selectedPreset === preset && !useCustom ? 'bg-white shadow text-gray-900' : 'text-gray-600 hover:text-gray-900'}`}>
                <div className="text-xl mb-1">{preset === 'snel' ? '⚡' : preset === 'aanbevolen' ? '⭐' : '💰'}</div>
                <div className="text-sm">{preset === 'snel' ? 'Snel' : preset === 'aanbevolen' ? 'Aanbevolen' : 'Max'}</div>
              </button>
            ))}
          </div>
          <div className={`${WARM_HIGHLIGHT_CLASS} p-6 mb-4`}>
            <div className="text-center">
              <div className="text-sm text-gray-600 mb-2">Je ontvangt</div>
              <div className="text-4xl font-bold text-gray-900 mb-2">{formatPrice(sellerPayout)}</div>
              <div className="text-sm text-gray-600">Na verificatie zichtbaar op de marktplaats</div>
            </div>
          </div>
          <div className="border-2 border-gray-200 rounded-lg p-4">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input type="checkbox" checked={useCustom} onChange={(e) => setUseCustom(e.target.checked)} className="w-5 h-5 text-red-500 rounded focus:ring-red-500" />
              <span className="font-semibold text-gray-900">Of kies eigen bedrag</span>
            </label>
            {useCustom && (
              <div className="mt-4">
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">€</span>
                  <input type="number" value={customAmount} onChange={(e) => setCustomAmount(e.target.value)} placeholder="0.00" step="0.01" min="1" max={maxSellerReceives.toFixed(2)}
                    className={`w-full pl-8 pr-4 py-3 border-2 rounded-lg focus:outline-none text-lg ${isValidCustomAmount() ? 'border-gray-300 focus:border-red-500' : 'border-red-300 focus:border-red-500'}`} />
                </div>
                <div className="mt-2 text-sm text-gray-600">Maximum: {formatPrice(maxSellerReceives)}</div>
                {!isValidCustomAmount() && (<div className="mt-2 text-sm text-red-600">Bedrag moet tussen {formatPrice(1)} en {formatPrice(maxSellerReceives)} zijn</div>)}
              </div>
            )}
          </div>
        </div>
        <button onClick={() => setShowSpecification(!showSpecification)} className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-6 transition">
          <Info className="w-5 h-5" />
          <span className="text-sm font-medium">Bekijk de specificatie</span>
        </button>
        {showSpecification && (
          <div className="bg-gray-50 rounded-lg p-4 mb-6 text-sm">
            <div className="space-y-2">
              <div className="flex justify-between"><span className="text-gray-600">Prijs voor de koper:</span><span className="font-semibold">{formatPrice(buyerTotal)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">iDEAL kosten:</span><span className="font-semibold text-gray-700">{formatPrice(CHECKOUT_FEE)}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Platformkosten (4,5%):</span><span className="font-semibold text-red-600">- {formatPrice(platformFee)}</span></div>
              <div className="border-t border-gray-300 pt-2 flex justify-between"><span className="font-semibold text-gray-900">Dit krijg jij uitbetaald:</span><span className="font-bold text-red-600">{formatPrice(sellerPayout)}</span></div>
            </div>
          </div>
        )}
        <button onClick={handleNext} disabled={!brand || !isValidCustomAmount()}
          className={`w-full py-4 rounded-xl text-lg ${brand && isValidCustomAmount() ? PRIMARY_BUTTON_CLASS : `font-bold transition ${PRIMARY_BUTTON_DISABLED}`}`}>
          Volgende
        </button>
      </div>
    </div>
  );
};

const SellStep2 = ({ initialData = {}, onNext, onBack }) => {
  const [mode, setMode] = useState('login');
  const [view, setView] = useState('auth');
  const [email, setEmail] = useState(initialData.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState(initialData.displayName || '');
  const nameParts = (initialData.displayName || '').trim().split(/\s+/).filter(Boolean);
  const [merchantFirstName, setMerchantFirstName] = useState(nameParts.slice(0, -1).join(' ') || nameParts[0] || '');
  const [merchantLastName, setMerchantLastName] = useState(nameParts.length > 1 ? nameParts[nameParts.length - 1] : '');
  const [addressLine1, setAddressLine1] = useState('');
  const [zipcode, setZipcode] = useState('');
  const [city, setCity] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptOppTerms, setAcceptOppTerms] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showMerchantSetup, setShowMerchantSetup] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [verificationEmail, setVerificationEmail] = useState(initialData.email || '');
  const [resetEmail, setResetEmail] = useState(initialData.email || '');

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const resetMessages = () => {
    setError('');
    setInfoMessage('');
  };

  const proceedWithVerifiedUser = useCallback(async (user) => {
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const userData = userDoc.exists() ? userDoc.data() : {};

    if (!userData?.opp_merchant_uid) {
      setShowMerchantSetup(true);
      setView('auth');
      return;
    }

    onNext({
      email: user.email,
      displayName: user.displayName,
      uid: user.uid,
      isLoggedIn: true,
      hasMerchant: true,
    });
  }, [onNext]);

  useEffect(() => {
    const checkMerchantStatus = async () => {
      if (!initialData.isLoggedIn) return;

      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return;

      setCurrentUser(user);
      setEmail(user.email || '');
      setDisplayName(user.displayName || '');
      const currentNameParts = (user.displayName || '').trim().split(/\s+/).filter(Boolean);
      setMerchantFirstName(currentNameParts.slice(0, -1).join(' ') || currentNameParts[0] || '');
      setMerchantLastName(currentNameParts.length > 1 ? currentNameParts[currentNameParts.length - 1] : '');

      try {
        if (!user.emailVerified) {
          setVerificationEmail(user.email || '');
          setView('verify');
          return;
        }

        await proceedWithVerifiedUser(user);
      } catch (err) {
        console.error('Firestore error:', err);
        setError(err.message || 'Er ging iets mis bij het ophalen van je account.');
      }
    };

    checkMerchantStatus();
  }, [initialData.isLoggedIn, proceedWithVerifiedUser]);

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);

    try {
      if (!isValidEmail(email)) throw new Error('Voer een geldig e-mailadres in');
      if (password.length < 6) throw new Error('Wachtwoord moet minimaal 6 tekens zijn');

      let user;

      if (mode === 'signup') {
        if (password !== confirmPassword) throw new Error('Wachtwoorden komen niet overeen');
        if (!acceptTerms) throw new Error('Je moet akkoord gaan met de voorwaarden');
        if (!displayName.trim()) throw new Error('Voer je naam in');

        user = await signUp(email, password, displayName.trim());
        setCurrentUser(user);
        setVerificationEmail(user.email || email);
        setView('verify');
        setPassword('');
        setConfirmPassword('');
        try {
          await sendCustomVerificationEmail('/sell');
          setInfoMessage('We hebben een verificatie-e-mail gestuurd. Check ook je spam.');
        } catch (emailError) {
          setError(emailError.message || 'Je account is aangemaakt, maar we konden de verificatie-e-mail niet versturen.');
        }
        return;
      }

      user = await signIn(email, password);
      setCurrentUser(user);

      if (!user.emailVerified) {
        setVerificationEmail(user.email || email);
        setView('verify');
        return;
      }

      await proceedWithVerifiedUser(user);
    } catch (authError) {
      console.error('Auth/Merchant error:', authError);
      setError(authError.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    resetMessages();
    setLoading(true);

    try {
      if (!isValidEmail(resetEmail)) {
        throw new Error('Voer een geldig e-mailadres in');
      }

      const result = await sendCustomPasswordResetEmail(resetEmail, '/login');
      setInfoMessage(
        result?.message || 'We hebben een e-mail gestuurd waarmee je je wachtwoord kunt resetten. Check ook je spam.',
      );
    } catch (resetError) {
      setError(resetError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    resetMessages();
    setLoading(true);

    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error('Je sessie is verlopen. Log opnieuw in om verder te gaan.');
      }

      const result = await sendCustomVerificationEmail('/sell');
      setInfoMessage(
        result?.alreadyVerified
          ? 'Je e-mailadres is al geverifieerd.'
          : 'We hebben de verificatie-e-mail opnieuw verstuurd. Check ook je spam.',
      );
    } catch (verificationError) {
      setError(verificationError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationCheck = async () => {
    resetMessages();
    setLoading(true);

    try {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error('Je sessie is verlopen. Log opnieuw in en probeer het opnieuw.');
      }

      await user.reload();

      if (!auth.currentUser?.emailVerified) {
        throw new Error('Je e-mail is nog niet geverifieerd. Check je inbox en spam.');
      }

      const verifiedUser = auth.currentUser;
      await verifiedUser.getIdToken(true);
      setCurrentUser(verifiedUser);
      setShowMerchantSetup(false);
      await proceedWithVerifiedUser(verifiedUser);
    } catch (verificationError) {
      setError(verificationError.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMerchantSetup = async () => {
    if (!currentUser?.emailVerified) {
      resetMessages();
      setVerificationEmail(currentUser?.email || email);
      setView('verify');
      return;
    }

    if (!acceptOppTerms) {
      setError('Ga akkoord met de voorwaarden van onze betaalpartner om je verkoopaccount aan te maken.');
      return;
    }

    if (!merchantFirstName.trim() || !merchantLastName.trim()) {
      setError('Vul je voor- en achternaam in.');
      return;
    }

    if (!addressLine1.trim() || !zipcode.trim() || !city.trim()) {
      setError('Vul je adres, postcode en woonplaats in.');
      return;
    }

    resetMessages();
    setLoading(true);

    try {
      await currentUser.reload();
      await currentUser.getIdToken(true);

      const createMerchant = httpsCallable(functions, 'createMerchantAccount');
      const result = await createMerchant({
        oppTermsAccepted: true,
        merchantProfile: {
          firstName: merchantFirstName.trim(),
          lastName: merchantLastName.trim(),
          addressLine1: addressLine1.trim(),
          zipcode: zipcode.trim().toUpperCase(),
          city: city.trim(),
        },
      });

      onNext({
        email: currentUser.email,
        displayName: currentUser.displayName,
        uid: currentUser.uid,
        isLoggedIn: true,
        hasMerchant: true,
        verification_url: result.data.verification_url,
        merchant_uid: result.data.merchant_uid,
      });
    } catch (merchantError) {
      console.error('Merchant setup error:', merchantError);
      setError(merchantError.message || 'Er ging iets mis bij het aanmaken van je verkoopaccount');
      setLoading(false);
    }
  };

  if (view === 'verify') {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Check je e-mail</h1>
          <p className="text-lg text-gray-600">
            We hebben een verificatie-e-mail gestuurd naar {verificationEmail || email}.
          </p>
        </div>

        <div className={`${WARM_CARD_CLASS} p-8`}>
          <div className="rounded-xl border border-orange-100 bg-orange-50 p-5 text-sm text-gray-700">
            Klik op de link in de e-mail om je account te activeren. Daarna kun je verder met verkopen.
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {infoMessage && (
            <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg text-orange-700 text-sm">
              {infoMessage}
            </div>
          )}

          <div className="mt-6 space-y-3">
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={loading}
              className="w-full py-4 px-6 border border-orange-200 text-orange-700 bg-white rounded-xl font-semibold hover:bg-orange-50 transition disabled:opacity-50"
            >
              Verificatie-e-mail opnieuw versturen
            </button>
            <button
              type="button"
              onClick={handleVerificationCheck}
              disabled={loading}
              className={`w-full py-4 px-6 ${PRIMARY_BUTTON_CLASS} disabled:opacity-50`}
            >
              {loading ? 'Bezig...' : 'Ik heb mijn e-mail geverifieerd'}
            </button>
            <button
              type="button"
              onClick={onBack}
              disabled={loading}
              className="w-full py-4 px-6 border-2 border-gray-300 rounded-xl font-bold hover:border-red-500 transition disabled:opacity-50"
            >
              Terug
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showMerchantSetup) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Verkoopaccount instellen</h1>
          <p className="text-lg text-gray-600">Eenmalige setup om te kunnen verkopen</p>
        </div>
        <div className={`${WARM_CARD_CLASS} p-8`}>
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 mb-6">
            <h3 className="font-bold text-orange-900 mb-2 flex items-center">
              <span className="text-2xl mr-2">✓</span>
              Bijna klaar om te verkopen
            </h3>
            <p className="text-sm text-orange-800">
              We maken je verkoopaccount aan bij onze betalingspartner. Je hoeft alleen nog akkoord te gaan met de voorwaarden.
            </p>
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-gray-900 font-semibold mb-2">Voornaam *</label>
              <input
                type="text"
                value={merchantFirstName}
                onChange={(e) => setMerchantFirstName(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-gray-900 font-semibold mb-2">Achternaam *</label>
              <input
                type="text"
                value={merchantLastName}
                onChange={(e) => setMerchantLastName(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                disabled={loading}
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-gray-900 font-semibold mb-2">Adres *</label>
              <input
                type="text"
                value={addressLine1}
                onChange={(e) => setAddressLine1(e.target.value)}
                placeholder="Straat en huisnummer"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-gray-900 font-semibold mb-2">Postcode *</label>
              <input
                type="text"
                value={zipcode}
                onChange={(e) => setZipcode(e.target.value)}
                placeholder="1234 AB"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                disabled={loading}
              />
            </div>
            <div>
              <label className="block text-gray-900 font-semibold mb-2">Woonplaats *</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                disabled={loading}
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={acceptOppTerms}
                onChange={(e) => setAcceptOppTerms(e.target.checked)}
                className="w-5 h-5 text-red-500 rounded mt-1 focus:ring-red-500"
                disabled={loading}
              />
              <span className="text-sm text-gray-700">
                Ik ga akkoord met de voorwaarden van{' '}
                <a
                  href="https://onlinepaymentplatform.com/terms-policies"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-500 hover:underline font-semibold"
                >
                  Online Payment Platform
                </a>
                {', onze betaalpartner.'}
              </span>
            </label>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {infoMessage && (
            <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg text-orange-700 text-sm">
              {infoMessage}
            </div>
          )}

          <div className="flex gap-4">
            <button
              type="button"
              onClick={onBack}
              disabled={loading}
              className="flex-1 py-4 px-6 border-2 border-gray-300 rounded-xl font-bold hover:border-red-500 transition disabled:opacity-50"
            >
              Terug
            </button>
            <button
              onClick={handleMerchantSetup}
              disabled={
                loading ||
                !acceptOppTerms ||
                !merchantFirstName.trim() ||
                !merchantLastName.trim() ||
                !addressLine1.trim() ||
                !zipcode.trim() ||
                !city.trim()
              }
              className={`flex-1 py-4 px-6 rounded-xl font-bold transition ${
                (
                  acceptOppTerms &&
                  merchantFirstName.trim() &&
                  merchantLastName.trim() &&
                  addressLine1.trim() &&
                  zipcode.trim() &&
                  city.trim() &&
                  !loading
                )
                  ? PRIMARY_BUTTON_ENABLED
                  : PRIMARY_BUTTON_DISABLED
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Verwerken...
                </span>
              ) : (
                'Verkoopaccount aanmaken'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'reset') {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Wachtwoord resetten</h1>
          <p className="text-lg text-gray-600">Vul je e-mailadres in en we sturen je een resetlink.</p>
        </div>

        <div className={`${WARM_CARD_CLASS} p-8`}>
          <form onSubmit={handlePasswordReset}>
            <div className="mb-4">
              <label className="block text-gray-900 font-semibold mb-2">E-mailadres</label>
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="jouw@email.nl"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                required
              />
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {infoMessage && (
              <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg text-orange-700 text-sm">
                {infoMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-4 px-6 ${PRIMARY_BUTTON_CLASS} disabled:opacity-50`}
            >
              {loading ? 'Bezig...' : 'Stuur resetlink'}
            </button>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-center text-sm text-gray-600">
                <button
                  type="button"
                  onClick={() => {
                    resetMessages();
                    setView('auth');
                    setMode('login');
                  }}
                  className="text-red-500 hover:underline font-semibold"
                >
                  Terug naar inloggen
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-3">
          {mode === 'signup' ? 'Maak een account aan' : 'Log in op je account'}
        </h1>
        <p className="text-lg text-gray-600">Om je cadeaubon te verkopen hebben we een account nodig</p>
      </div>

      <div className={`${WARM_CARD_CLASS} p-8`}>
        <div className="flex bg-gray-100 rounded-lg p-1 mb-6">
          <button
            onClick={() => {
              resetMessages();
              setMode('login');
            }}
            className={`flex-1 py-3 rounded-lg font-semibold transition ${
              mode === 'login' ? 'bg-white text-gray-900 shadow' : 'text-gray-600'
            }`}
          >
            Inloggen
          </button>
          <button
            onClick={() => {
              resetMessages();
              setMode('signup');
            }}
            className={`flex-1 py-3 rounded-lg font-semibold transition ${
              mode === 'signup' ? 'bg-white text-gray-900 shadow' : 'text-gray-600'
            }`}
          >
            Registreren
          </button>
        </div>

        <form onSubmit={handleAuthSubmit}>
          {mode === 'signup' && (
            <div className="mb-4">
              <label className="block text-gray-900 font-semibold mb-2">Volledige naam *</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Voor- en achternaam"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                required
              />
            </div>
          )}

          <div className="mb-4">
            <label className="block text-gray-900 font-semibold mb-2">E-mailadres *</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jouw@email.nl"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
              required
            />
          </div>

          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-gray-900 font-semibold">Wachtwoord *</label>
              {mode === 'login' && (
                <button
                  type="button"
                  onClick={() => {
                    resetMessages();
                    setResetEmail(email);
                    setView('reset');
                  }}
                  className="text-sm text-red-500 hover:underline"
                >
                  Wachtwoord vergeten?
                </button>
              )}
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimaal 6 tekens"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
              required
            />
          </div>

          {mode === 'signup' && (
            <>
              <div className="mb-4">
                <label className="block text-gray-900 font-semibold mb-2">Bevestig wachtwoord *</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Herhaal je wachtwoord"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none"
                  required
                />
              </div>

              <div className="mb-6">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="w-5 h-5 text-red-500 rounded mt-1 focus:ring-red-500"
                  />
                  <span className="text-sm text-gray-700">
                    Ik ga akkoord met de <Link to="/terms" className="text-red-500 hover:underline">algemene voorwaarden</Link> en het <Link to="/privacy" className="text-red-500 hover:underline">privacy beleid</Link>
                  </span>
                </label>
              </div>
            </>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {infoMessage && (
            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg text-orange-700 text-sm">
              {infoMessage}
            </div>
          )}

          <div className="flex gap-4">
            <button
              type="button"
              onClick={onBack}
              disabled={loading}
              className="flex-1 py-4 px-6 border-2 border-gray-300 rounded-xl font-bold hover:border-red-500 transition disabled:opacity-50"
            >
              Terug
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 py-4 px-6 ${PRIMARY_BUTTON_CLASS} disabled:opacity-50`}
            >
              {loading ? 'Bezig...' : (mode === 'login' ? 'Inloggen' : 'Registreren')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================================
// STEP 3: Verification
// ============================================================================
const SellStep3 = ({ initialData = {}, onNext, onBack }) => {
  const [cardPhoto, setCardPhoto] = useState(initialData.cardPhoto || null);
  const [cardNumber, setCardNumber] = useState(initialData.cardNumber || '');
  const [pinCode, setPinCode] = useState(initialData.pinCode || '');
  const [fullName, setFullName] = useState(initialData.fullName || '');
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = React.useRef(null); // <-- Use React.useRef

  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") { setDragActive(true); }
    else if (e.type === "dragleave") { setDragActive(false); }
  };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) { handleFile(e.dataTransfer.files[0]); }
  };
  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) { handleFile(e.target.files[0]); }
  };
  const handleFile = (file) => {
    if (!file.type.startsWith('image/')) { setError('Upload alleen afbeeldingen'); return; }
    if (file.size > 10 * 1024 * 1024) { setError('Afbeelding te groot (max 10MB)'); return; }
    setError('');
    const reader = new FileReader();
    reader.onload = (e) => { setCardPhoto({ file: file, preview: e.target.result, name: file.name }); };
    reader.readAsDataURL(file);
  };
  const removePhoto = () => { setCardPhoto(null); if (fileInputRef.current) { fileInputRef.current.value = ''; } };
  const formatCardNumber = (value) => { return value.replace(/[^A-Za-z0-9-]/g, '').toUpperCase(); };
  const handleCardNumberChange = (e) => {setCardNumber(formatCardNumber(e.target.value)); };
  const validateCardNumber = () => { const cleaned = cardNumber.replace(/[\s-]/g, ''); return cleaned.length >= 8 && cleaned.length <= 30; };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!cardPhoto) { setError('Upload een foto'); return; }
    if (!validateCardNumber()) { setError('Voer een geldige code in (minimaal 8 tekens)'); return; }
    if (!fullName.trim()) { setError('Voer je naam in'); return; }
    onNext({ cardPhoto, cardNumber, pinCode, fullName });
  };

  const handleBackClick = () => {
    if (onBack && typeof onBack === 'function') {
      onBack();
    } else {
      console.error('onBack is not a function:', onBack);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-3">Verifieer je cadeaubon</h1>
        <p className="text-lg text-gray-600">Upload een foto van je cadeaubon en vul de code in.</p>
      </div>
      <div className={`${WARM_CARD_CLASS} p-8`}>
        <form onSubmit={handleSubmit}>
          <div className="mb-6">
            <label className="block text-gray-900 font-semibold mb-2">Foto van cadeaubon *</label>
            {!cardPhoto ? (
              <div onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition ${dragActive ? 'border-orange-400 bg-orange-50' : 'border-gray-300 hover:border-orange-300 hover:bg-orange-50/40'}`}>
                <div className="flex flex-col items-center">
                  <svg className="w-12 h-12 text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                  <p className="text-gray-700 font-semibold mb-1">Klik om te uploaden of sleep hier</p>
                  <p className="text-sm text-gray-500">JPG, PNG (max 10MB)</p>
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileInput} className="hidden" />
              </div>
            ) : (
              <div className="border-2 border-gray-200 rounded-xl p-4">
                <div className="flex items-start space-x-4">
                  <img src={cardPhoto.preview} alt="Preview" className="w-32 h-32 object-cover rounded-lg" />
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 mb-1">{cardPhoto.name}</p>
                    <p className="text-sm text-red-600 mb-3">✓ Upload toegevoegd</p>
                    <button type="button" onClick={removePhoto} className="text-sm text-red-600 hover:text-red-700 font-semibold">Verwijderen</button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="mb-6">
            <label className="block text-gray-900 font-semibold mb-2">Cadeaubon code *</label>
            <input type="text" value={cardNumber} onChange={handleCardNumberChange} placeholder="1234 5678 9012 3456" maxLength={30}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none text-lg font-mono" required />
          </div>
          <div className="mb-6">
            <label className="block text-gray-900 font-semibold mb-2">Pincode (optioneel)</label>
            <input type="text" value={pinCode} onChange={(e) => setPinCode(e.target.value.replace(/\D/g, '').slice(0, 8))} placeholder="1234" maxLength={8}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none text-lg font-mono" />
          </div>
          <div className="mb-6">
            <label className="block text-gray-900 font-semibold mb-2">Jouw volledige naam *</label>
            <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Voor- en achternaam"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-red-500 focus:outline-none text-lg" required />
          </div>
          {error && (<div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>)}
          <div className="flex gap-4">
            <button type="button" onClick={handleBackClick} className="flex-1 py-4 px-6 border-2 border-gray-300 rounded-xl font-bold hover:border-red-500 transition">Terug</button>
            <button type="submit" className={`flex-1 py-4 px-6 ${PRIMARY_BUTTON_CLASS}`}>Volgende</button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================================
// HELPER COMPONENT: StepItem (Fixes the "not defined" error)
// ============================================================================
const StepItem = ({ number, title, status, desc }) => {
  const isComplete = status === 'complete';
  const isActive = status === 'action';
  const isLocked = status === 'locked';

  return (
    <div className="flex items-start space-x-4 p-4 rounded-xl border border-gray-100 bg-white">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 font-bold ${
        isComplete ? 'bg-orange-100 text-red-600' : 
        isActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'
      }`}>
        {isComplete ? <CheckCircle2 className="w-5 h-5" /> : number}
      </div>
      <div>
        <h4 className={`font-bold ${isLocked ? 'text-gray-400' : 'text-gray-900'}`}>{title}</h4>
        <p className="text-sm text-gray-500">{desc}</p>
      </div>
      {isLocked && <Lock className="w-4 h-4 text-gray-300 ml-auto" />}
    </div>
  );
};

// ============================================================================
// STEP 4: Confirm & Submit 
// ============================================================================
const SellStep4 = ({ initialData = {}, onSubmit, onBack }) => {
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (loading) return; // Prevent double clicks
    
    if (!agreedToTerms) {
      setError('Je moet akkoord gaan met de voorwaarden');
      return;
    }
     
    setError('');
    setLoading(true);

    try {
      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error('Je sessie is verlopen. Log opnieuw in om je listing in te dienen.');
      }

      await currentUser.reload();

      if (!auth.currentUser?.emailVerified) {
        throw new Error('Verifieer eerst je e-mailadres voordat je je listing indient.');
      }

      await auth.currentUser.getIdToken(true);

      // Upload card photo
      let imageUrl = null;
      let imagePath = null;
      
      if (initialData.cardPhoto && initialData.cardPhoto.file) {
        const uploadResult = await uploadGiftCardImage(
          initialData.uid, 
          `temp_${Date.now()}`,
          initialData.cardPhoto.file
        );
        imageUrl = uploadResult.url;
        imagePath = uploadResult.path;
      }

      // Create gift card
      const giftCardData = {
        brand: initialData.brand,
        cardValue: initialData.cardValue,
        sellingPrice: initialData.buyerPrice,
        sellerPayout: initialData.sellerPayout,
        platformFee: calculatePlatformFee(initialData.sellerPayout),
        discount: Math.round(((initialData.cardValue - initialData.buyerPrice) / initialData.cardValue) * 100),
        cardNumber: initialData.cardNumber,
        pinCode: initialData.pinCode || null,
        sellerId: initialData.uid,
        sellerName: initialData.fullName,
        sellerEmail: initialData.email,
        imageUrl: imageUrl,
        imagePath: imagePath,
        category: getCategoryForBrand(initialData.brand),
        popular: isPopularBrand(initialData.brand),
      };

      const cardId = await createGiftCard(giftCardData);
      
      onSubmit({
        ...initialData,
        cardId: cardId,
        success: true
      });

    } catch (error) {
      console.error('Error:', error);
      setError(error.message || 'Er is een fout opgetreden. Probeer het opnieuw.');
      setLoading(false);
    }
  };

  // Helper functions
  const getCategoryForBrand = (brand) => {
    const categories = {
      'Bol.com': 'shopping',
      'Albert Heijn': 'food',
      'Coolblue': 'electronics',
      'Douglas': 'Beauty & Wellness',
    };
    return categories[brand] || 'shopping';
  };
  
  const isPopularBrand = (brand) => {
    const popularBrands = ['Albert Heijn', 'Bol.com', 'Coolblue', 'Douglas'];
    return popularBrands.includes(brand);
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-3">
          Controleer je gegevens
        </h1>
        <p className="text-lg text-gray-600">
          Controleer of alles klopt voordat je je bon indient.
        </p>
      </div>

      {/* Review Card - Show card details */}
      <div className={`${WARM_CARD_CLASS} overflow-hidden mb-6`}>
        <div className="bg-gradient-to-r from-red-500 to-orange-500 px-6 py-4">
          <h2 className="text-xl font-bold text-white">Je aanbieding</h2>
        </div>
        <div className="p-6 space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Cadeaubon</h3>
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-700">Merk:</span>
                <span className="font-bold text-gray-900">{initialData.brand}</span>
              </div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-gray-700">Waarde:</span>
                <span className="font-bold text-gray-900">{formatPrice(initialData.cardValue || 0)}</span>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Uitbetaling</h3>
            <div className={`${WARM_HIGHLIGHT_CLASS} p-4`}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-gray-700">Dit krijg jij uitbetaald:</span>
                <span className="text-2xl font-bold text-red-600">{formatPrice(initialData.sellerPayout || 0)}</span>
              </div>
              <div className="text-sm text-gray-600">
                <p>Na goedkeuring door ons team</p>
              </div>
            </div>
          </div>

          {initialData.cardPhoto && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Geüploade foto</h3>
              <img 
                src={initialData.cardPhoto.preview} 
                alt="Card preview" 
                className="rounded-lg border-2 border-gray-200 max-w-sm"
              />
            </div>
          )}
        </div>
      </div>

      {/* Important Notice */}
      <div className="rounded-2xl border border-orange-100 bg-orange-50 p-6 mb-6">
        <h3 className="font-bold text-slate-900 mb-2">Let op!</h3>
        <ul className="space-y-2 text-sm text-slate-700">
          <li>• Ons team controleert je cadeaubon. Dit duurt meestal minder dan 24 uur.</li>
          <li>• Na goedkeuring wordt je bon zichtbaar op de marktplaats.</li>
          <li>• Je ontvangt een e-mail zodra je bon is goedgekeurd of afgewezen.</li>
          <li>• Uitbetaling volgt na verkoop en de verificatieperiode.</li>
        </ul>
      </div>

      {/* Terms Agreement */}
      <div className={`${WARM_CARD_CLASS} p-6 mb-6`}>
        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => setAgreedToTerms(e.target.checked)}
            className="w-5 h-5 text-red-500 rounded mt-1 focus:ring-red-500"
            disabled={loading}
          />
          <div className="flex-1">
            <span className="text-gray-900">
              Ik bevestig dat alle opgegeven informatie correct is en ga akkoord met de{' '}
              <Link to="/terms" className="text-red-500 hover:underline font-semibold">
                Algemene Voorwaarden
              </Link>
              {' en het '}
              <Link to="/privacy" className="text-red-500 hover:underline font-semibold">
                Privacybeleid
              </Link>
              {' van Swapify Marketplace.'}
            </span>
          </div>
        </label>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Buttons */}
      <div className="flex gap-4">
        <button
          onClick={onBack}
          disabled={loading}
          className="flex-1 py-4 px-6 border-2 border-gray-300 rounded-xl font-bold hover:border-red-500 transition disabled:opacity-50"
        >
          Terug
        </button>
        <button
          onClick={handleSubmit}
          disabled={!agreedToTerms || loading}
          className={`flex-1 py-4 px-6 rounded-xl font-bold text-lg transition ${
            (agreedToTerms && !loading)
              ? PRIMARY_BUTTON_ENABLED
              : PRIMARY_BUTTON_DISABLED
          }`}
        >
          {loading ? (
            <span className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
              Verwerken...
            </span>
          ) : (
            'Indienen voor goedkeuring'
          )}
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// SUCCESS STEP
// ============================================================================
const SellSuccessStep = () => {
  return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <span className="text-4xl">🎉</span>
        </div>
        <h1 className="text-3xl font-bold text-gray-900">Je bon is ontvangen!</h1>
        <p className="text-gray-600 mt-2">We controleren je cadeaubon en laten je weten wanneer deze live staat.</p>
      </div>

      <div className="space-y-4">
        <StepItem 
          number="1" 
          title="Admin Controle" 
          status="pending" 
          desc="Ons team controleert je bon. Meestal binnen 24 uur."
        />
        <StepItem
          number="2"
          title="Bankrekening toevoegen"
          status="action"
          desc="Voeg je IBAN toe in je dashboard zodat we je kunnen uitbetalen."
        />
        <StepItem
          number="3" 
          title="Uitbetaling"
          status="locked" 
          desc="Wij regelen je uitbetaling zodra de verificatieperiode klaar is."
        />
      </div>
      
      <div className="mt-8 flex gap-4">
         <Link to="/marketplace" className="flex-1 text-center py-3 bg-gray-100 rounded-xl font-semibold">Marktplaats</Link>
         <Link to="/dashboard" className={`flex-1 text-center py-3 rounded-xl font-semibold ${PRIMARY_BUTTON_ENABLED}`}>Mijn Advertenties</Link>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN SELL PAGE COMPONENT
// ============================================================================
const SellPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    brand: '', cardValue: 50, selectedPreset: 'aanbevolen', customAmount: '', useCustom: false, sellerPayout: 0, buyerPrice: 0,
    email: '', password: '', isLoggedIn: false, uid: null, displayName: '',
    cardPhoto: null, cardNumber: '', pinCode: '', fullName: '',
  });

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        setFormData(prevData => ({
          ...prevData,
          isLoggedIn: true,
          email: user.email,
          uid: user.uid,
          displayName: user.displayName || '',
          fullName: user.displayName || '',
        }));
      } else {
        setFormData(prevData => ({
          ...prevData,
          isLoggedIn: false,
          email: '',
          uid: null,
          displayName: '',
        }));
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const step = params.get('step');
    if (step) {
      setCurrentStep(parseInt(step, 10));
    }
  }, []);


  const handleStepComplete = (stepData) => {
    setFormData({ ...formData, ...stepData });
    setCurrentStep(currentStep + 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSubmit = async (finalData) => {
    const completeData = { ...formData, ...finalData };
    
    if (completeData.success) {
      setCurrentStep(5); // 5 is the new success step
      setFormData(completeData);
    }
  };

  // --- UPDATED STEPS: Removed Step 4 (IBAN) ---
  const steps = [
    { number: 1, label: 'Details' },
    { number: 2, label: 'Account' },
    { number: 3, label: 'Verificatie' },
    { number: 4, label: 'Bevestigen' }, // This is the new SellStep4 (was 5)
    { number: 5, label: 'Voltooid' }  // This is the new SellSuccessStep (was 6)
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50">
      <Navbar />

      <div className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
          <div className="flex items-center justify-between mb-2">
            {steps.filter(s => s.number <= 4).map((step, index) => ( // Filter to 4
              <React.Fragment key={step.number}>
                <div className="flex items-center space-x-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                    step.number === currentStep ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white' :
                    step.number < currentStep ? 'bg-orange-500 text-white' : 'bg-gray-300 text-gray-600'
                  }`}>
                    {step.number < currentStep ? '✓' : step.number}
                  </div>
                  <span className={`text-sm hidden sm:inline ${step.number === currentStep ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{step.label}</span>
                </div>
                {index < 3 && ( // Changed to 3
                  <div className="flex-1 h-1 mx-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className={`h-full transition-all duration-300 ${step.number < currentStep ? 'bg-orange-500' : 'bg-gray-200'}`}
                      style={{ width: step.number < currentStep ? '100%' : '0%' }}></div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Step Components - UPDATED */}
        {currentStep === 1 && <SellStep1 initialData={formData} onNext={handleStepComplete} />}
        {currentStep === 2 && <SellStep2 initialData={formData} onNext={handleStepComplete} onBack={handleBack} />}
        {currentStep === 3 && <SellStep3 initialData={formData} onNext={handleStepComplete} onBack={handleBack} />}
        {currentStep === 4 && <SellStep4 initialData={formData} onSubmit={handleSubmit} onBack={handleBack} />} 
        {currentStep === 5 && <SellSuccessStep />}
      </div>
    </div>
  );
};

export default SellPage;
